import type {
  CampaignPreflightAction,
  CampaignPreflightCheck,
  CampaignReview,
  CampaignReviewSeverity,
} from '../../api/types';

/**
 * The pre-flight review's pure half: how its rows are made, counted and
 * acted on. Kept apart from CampaignPreflight.tsx so the rules — which rows
 * are AI and which are facts, what counts as "worth a look", where a
 * suggestion may be applied — are tested rather than read off a screen.
 */

/** `note` is the AI's lowest severity: worth reading, not worth counting. */
export type PreflightLevel = 'ok' | 'warn' | 'risk' | 'unknown' | 'note';
export type PreflightGroup = 'sender' | 'audience' | 'words' | 'sending';

/** What a row can do: the server's actions, plus the ones only the builder knows. */
export type PreflightAction =
  | CampaignPreflightAction
  | { kind: 'gsm_fix' }
  | { kind: 'unsubscribe_on' }
  | { kind: 'set_window' }
  | { kind: 'retry_ai' }
  | { kind: 'use_suggestion'; evidence: string; suggestion: string };

export interface PreflightRow {
  id: string;
  group: PreflightGroup;
  level: PreflightLevel;
  title: string;
  detail: string;
  action?: PreflightAction;
  /** A second opinion from the model, badged as such — never a measured fact. */
  ai?: boolean;
  /** Replacement words the model offered for `evidence`, shown as "Try: …". */
  suggestion?: { evidence: string; text: string };
}

const SEVERITY_LEVEL: Record<CampaignReviewSeverity, PreflightLevel> = {
  stop: 'risk',
  warn: 'warn',
  note: 'note',
};

/** The server's measured rows, as rows. */
export function measuredRows(checks: CampaignPreflightCheck[]): PreflightRow[] {
  return checks.map((check) => ({
    id: `m:${check.id}`,
    group: check.group,
    level: check.level,
    title: check.title,
    detail: check.detail,
    ...(check.action ? { action: check.action } : {}),
  }));
}

/**
 * The words review as rows. The findings the server settled in code (links,
 * tags — `measured`) are facts and carry no badge; the model's are badged and
 * carry its suggestion when it offered one. On a text the measured COST
 * findings are dropped: the builder prices the body itself, live, and the same
 * fact twice would count as two things to fix.
 *
 * With nothing found, one row each says so — the facts that were checked, and
 * the model's own one-line verdict — so an empty group never reads as "not
 * looked at".
 */
export function reviewRows(review: CampaignReview, { sms }: { sms: boolean }): PreflightRow[] {
  const rows: PreflightRow[] = [];
  const findings = review.findings.filter((finding) => !(sms && finding.measured && finding.area === 'cost'));
  const measured = findings.filter((finding) => finding.measured);
  const opinions = findings.filter((finding) => !finding.measured);
  measured.forEach((finding, index) => {
    rows.push({
      id: `w:m${index}`,
      group: 'words',
      level: SEVERITY_LEVEL[finding.severity] ?? 'warn',
      title: finding.title,
      detail: finding.evidence ? `${finding.detail} (${finding.evidence})` : finding.detail,
    });
  });
  if (!measured.length) {
    const { links, tags } = review.checked;
    rows.push({
      id: 'w:checked',
      group: 'words',
      level: 'ok',
      title:
        links || tags
          ? `${links ? `${links} ${links === 1 ? 'link is' : 'links are'} well-formed` : 'No links'}${
              tags ? ' · every merge tag has a value' : ''
            }`
          : 'No links or merge tags to check',
      detail: 'Checked by the software, not guessed.',
    });
  }
  opinions.forEach((finding, index) => {
    rows.push({
      id: `w:ai${index}`,
      group: 'words',
      level: SEVERITY_LEVEL[finding.severity] ?? 'warn',
      title: finding.title,
      detail: finding.detail,
      ai: true,
      ...(finding.suggestion && finding.evidence
        ? { suggestion: { evidence: finding.evidence, text: finding.suggestion } }
        : {}),
    });
  });
  if (!opinions.length) {
    rows.push({
      id: 'w:ai-ok',
      group: 'words',
      level: 'ok',
      title: 'Nothing in the words to change',
      detail: review.summary || 'The AI read found nothing worth flagging.',
      ai: true,
    });
  }
  return rows;
}

export interface PreflightVerdict {
  level: 'ok' | 'warn' | 'risk';
  /** Rows at risk — fix before sending. */
  toFix: number;
  /** Rows worth a look: warnings, and checks that could not run. */
  toLook: number;
  passed: number;
}

/**
 * One verdict over every row. A check that could not run is worth a look,
 * never a pass — the screen saying "good to send" over a question it never
 * got answered is the exact failure the review exists to prevent. A `note` is
 * neither: it is read, not counted.
 */
export function verdictOf(rows: PreflightRow[]): PreflightVerdict {
  const toFix = rows.filter((row) => row.level === 'risk').length;
  const toLook = rows.filter((row) => row.level === 'warn' || row.level === 'unknown').length;
  const passed = rows.filter((row) => row.level === 'ok').length;
  return { level: toFix ? 'risk' : toLook ? 'warn' : 'ok', toFix, toLook, passed };
}

const things = (n: number) => `${n} ${n === 1 ? 'thing' : 'things'}`;

export function verdictTitle(verdict: PreflightVerdict): string {
  if (verdict.toFix) return `${things(verdict.toFix)} to fix before sending`;
  if (verdict.toLook) return `Good to send — ${things(verdict.toLook)} worth a look`;
  return 'Good to send';
}

/**
 * `text` with `find` replaced by `replacement`, or null when `find` is not in
 * it exactly once. "Use this" applies a suggestion only where it can be
 * placed without guessing: the words the model quoted, once, where they are —
 * never the first of two matches, which could be the wrong one.
 */
export function replaceOnce(text: string, find: string, replacement: string): string | null {
  if (!find) return null;
  const first = text.indexOf(find);
  if (first === -1 || text.indexOf(find, first + find.length) !== -1) return null;
  return text.slice(0, first) + replacement + text.slice(first + find.length);
}
